import { Box } from '../../render/parts'
import { Mug } from '../parts/props'
import { Laptop } from '../parts/tech'
import { DESK_HEIGHT, Grommet, ModestyPanel, WorkTop } from '../parts/worktop'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 1.2
const DEPTH = 0.6
const LEG = 0.045
/** Legs stand 4 mm proud of the top's underside so no faces end up coplanar. */
const LEG_HEIGHT = DESK_HEIGHT - 0.024
const LEG_INSET = 0.07
const LEG_X = WIDTH / 2 - LEG_INSET - LEG / 2
const LEG_Z = DEPTH / 2 - LEG_INSET - LEG / 2

/**
 * Small 1.2 x 0.6 desk on four slim square legs — the touchdown / hot-desk unit.
 * No pedestal, so the whole underside stays open. Origin at the centre of the
 * footprint, y = 0 on the floor, front facing +Z.
 */
function DeskCompact({ accent, variant = 'plain' }: AssetContext) {
  const dressed = variant === 'dressed'

  return (
    <group>
      <WorkTop width={WIDTH} depth={DEPTH} />

      {(
        [
          [-LEG_X, -LEG_Z],
          [LEG_X, -LEG_Z],
          [-LEG_X, LEG_Z],
          [LEG_X, LEG_Z],
        ] as const
      ).map(([x, z]) => (
        <Box
          key={`${x},${z}`}
          tone="shell"
          size={[LEG, LEG_HEIGHT, LEG]}
          position={[x, LEG_HEIGHT / 2, z]}
        />
      ))}

      {/* Back rail: braces the legs and screens the cables from the room side. */}
      <ModestyPanel width={WIDTH - 0.22} height={0.24} z={-DEPTH / 2 + 0.065} />
      <Grommet x={WIDTH / 2 - 0.18} z={-DEPTH / 2 + 0.12} />

      {dressed ? (
        <group>
          <group position={[-0.12, 0, 0.02]}>
            <Laptop base={DESK_HEIGHT} />
          </group>
          <group position={[0.3, 0, 0.1]}>
            <Mug base={DESK_HEIGHT} tone="accent" accent={accent} />
          </group>
        </group>
      ) : null}
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'desk-compact',
  name: 'Escritorio compacto',
  category: 'desks',
  footprint: [WIDTH, DEPTH],
  height: DESK_HEIGHT + 0.23,
  surfaceHeight: DESK_HEIGHT,
  seats: 1,
  occupantSide: 'front',
  accentable: true,
  variants: [
    { id: 'plain', name: 'Vacío' },
    { id: 'dressed', name: 'Equipado' },
  ],
  keywords: [
    'escritorio',
    'compacto',
    'pequeño',
    'mesa de trabajo',
    'compact desk',
    'small desk',
    'hot desk',
    'touchdown',
  ],
  Component: DeskCompact,
}
